import { ref } from 'vue'

export interface WrappedSvgResult {
  svg: string
  fontSize: number
  lines: string[]
  metrics: {
    width: number
    height: number
    lineHeight: number
  }
}

export function useAutoWrappedSvgText() {
  const lastResult = ref<WrappedSvgResult | null>(null)

  function measureText(text: string, fontSize: number): number {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    const textNode = document.createElementNS('http://www.w3.org/2000/svg', 'text')

    textNode.setAttribute('font-size', fontSize.toString())
    // textNode.setAttribute('font-family', 'Arial, Helvetica, sans-serif')
    textNode.setAttribute('font-family', 'Elza Condensed bold')

    textNode.setAttribute('font-weight', '800')
    textNode.setAttribute('font-style', 'normal')
    textNode.setAttribute('text-anchor', 'start')
    textNode.setAttribute('fill', 'ffffff')
    textNode.setAttribute('style', 'letter-spacing:-3px; text-transform:uppercase;')

    textNode.textContent = text

    svg.appendChild(textNode)
    svg.style.position = 'absolute'
    svg.style.visibility = 'hidden'

    document.body.appendChild(svg)
    const width = textNode.getBBox().width
    svg.remove()

    return width
  }

  function wrapText(text: string, fontSize: number, boxWidth: number): string[] {
    const words = text.split(/\s+/)
    const lines: string[] = []
    let line = ''

    for (const word of words) {
      const testLine = line ? line + ' ' + word : word
      const w = measureText(testLine, fontSize)

      if (w > boxWidth && line) {
        lines.push(line)
        line = word
      } else {
        line = testLine
      }
    }
    if (line) lines.push(line)

    return lines
  }

  function generateSvg(lines: string[], fontSize: number, lineHeight: number): string {
    const tspans = lines
      .map((line, i) => `<tspan x="0" dy="${i === 0 ? 0 : lineHeight}px">${line}</tspan>`)
      .join('')

    // return `<text font-size="${fontSize}" font-family="Arial, Helvetica, sans-serif"
    //      font-weight="950" font-style="oblique" text-anchor="start" fill="#ffffff"
    //       style="letter-spacing:-3px; text-transform:uppercase;" x="0" y="0">${tspans}</text>`

    return `<text font-size="${fontSize}" font-family="Elza Condensed bold" font-weight="800"
          text-anchor="start" fill="#ffffff"
          style="letter-spacing:-3px;" x="0" y="0">${tspans}</text>`
  }

  function createWrappedSvg(
    text: string,
    boxWidth: number,
    boxHeight: number,
    fontSizes: number[] = [24, 20, 18, 16, 14],
  ): WrappedSvgResult {
    for (const fontSize of fontSizes) {
      const lineHeight = fontSize * 1.2

      const lines = wrapText(text, fontSize, boxWidth)
      const height = lines.length * lineHeight

      if (height <= boxHeight) {
        const svg = generateSvg(lines, fontSize, lineHeight)
        const width = Math.min(...lines.map((line) => measureText(line, fontSize)))

        const result: WrappedSvgResult = {
          svg,
          fontSize,
          lines,
          metrics: { width, height, lineHeight },
        }

        lastResult.value = result
        return result
      }
    }

    // fallback: return smallest font size result
    const smallest = fontSizes[fontSizes.length - 1]!
    const lineHeight = smallest * 1.2
    const lines = wrapText(text, smallest, boxWidth)
    const svg = generateSvg(lines, smallest, lineHeight)

    const result: WrappedSvgResult = {
      svg,
      fontSize: smallest,
      lines,
      metrics: {
        width: boxWidth,
        height: lines.length * lineHeight,
        lineHeight,
      },
    }

    lastResult.value = result
    return result
  }

  return {
    createWrappedSvg,
    lastResult,
  }
}
